import { Result } from "antd";
import { AxiosError } from "axios";
import { ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { decodeJwtClaims } from "@lib/authentication/jwt";
import { clearAuthentication, setAuthentication } from "@lib/authentication/SessionStore";
import { maintenanceMinutes, maintenanceRetryAfter } from "../rest/maintenance";
import { fetchMe } from "./MeService";
import { isPlatformToken, realmRolesFromToken } from "./realm";
import {
  beginSubTenantSync, clearSubTenants, endSubTenantSync, getSelectedSubTenant,
  setAvailableSubTenants, setSubTenantProblem, SubTenantProblem, useSubTenant,
} from "./SubTenantStore";

type Props = { children: ReactNode };

const TERMINAL_CODES: readonly SubTenantProblem[] = ["SUBTENANT_REQUIRED", "USER_NOT_FOUND"];

/**
 * Riconosce le due risposte di `/me` da cui non si esce ritentando: il nucleo attivo
 * non è determinabile (409) o la persona non ha un profilo in QUESTO ambiente (404).
 * L'uscita è cambiare ambiente, quindi vanno distinte da un errore generico.
 */
function terminalProblem(error: unknown): SubTenantProblem | null {
  const body = (error as AxiosError<{ errorCode?: string }> | undefined)?.response?.data;
  const code = body?.errorCode;
  return TERMINAL_CODES.find((c) => c === code) ?? null;
}

/** Legge il claim `sub_tenants` (array di slug) dall'access token. */
function readSubTenants(accessToken: string): string[] {
  const claims = decodeJwtClaims<{ sub_tenants?: unknown }>(accessToken);
  return Array.isArray(claims?.sub_tenants) ? claims.sub_tenants.map(String) : [];
}

export function OidcSessionBridge({ children }: Props): ReactElement {
  const auth = useAuth();
  // Il nucleo attivo è una dipendenza dell'effect: `/me` risponde coi ruoli e
  // privilegi del nucleo, quindi cambiarlo obbliga a rileggerlo.
  const { selected } = useSubTenant();
  // Chiave dell'ultima fetch andata a buon fine (token + nucleo). Serve perché
  // `setAvailableSubTenants` gira DENTRO l'effect e riemette: senza un gate sul
  // cambio effettivo, ogni giro ne innescherebbe un altro.
  const lastFetchKey = useRef<string | null>(null);
  // Smontaggio ≠ sorpasso, e servono due guardie diverse. Questa segna solo lo
  // smontaggio definitivo: sta in un effect a dipendenze vuote apposta, perché una
  // cleanup legata alle dipendenze scatterebbe a ogni loro cambio e ucciderebbe la
  // fetch appena partita.
  const mounted = useRef<boolean>(true);
  // Secondi al prossimo tentativo mentre `/me` risponde 503 di manutenzione; null
  // fuori dalla manutenzione. Il tick è la dipendenza che fa ripartire l'effect.
  const [maintenance, setMaintenance] = useState<number | null>(null);
  const [retryTick, setRetryTick] = useState<number>(0);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (retryTimer.current) clearTimeout(retryTimer.current);
    };
  }, []);

  useEffect(() => {
    if (auth.isLoading || auth.activeNavigator) return;

    const user = auth.user;
    if (user && !user.expired && user.access_token) {
      // Sessione di piattaforma (realm non-tenant): /me è tenant-only → bootstrap
      // dal JWT, ruoli copiati verbatim, nessun sub-tenant.
      if (isPlatformToken(user.access_token)) {
        setAuthentication(user.access_token, realmRolesFromToken(user.access_token), []);
        clearSubTenants();
        return;
      }

      // Riconciliare lo store col claim PRIMA della fetch: `/me` passa
      // dall'ActiveSubTenantFilter e `fetchMe` legge di lì il suo header
      // `X-Tenant`. Fatto dopo, la prima richiesta partirebbe senza header e
      // tornerebbe coi ruoli del nucleo di default — o 409 se i nuclei sono ≥2
      // e 'prod' non c'è. `setAvailableSubTenants` conserva la scelta persistita
      // se è ancora nel claim: è la ragione per cui va usato lui e non
      // `pickDefault`, che riporterebbe a 'prod' a ogni reload.
      setAvailableSubTenants(readSubTenants(user.access_token));

      const fetchKey: string = `${user.access_token}|${getSelectedSubTenant() ?? ""}`;
      if (lastFetchKey.current === fetchKey) return;
      lastFetchKey.current = fetchKey;
      beginSubTenantSync();

      // La staleness si misura sulla CHIAVE, non su un flag di cleanup. Motivo:
      // `setAvailableSubTenants` gira dentro questo stesso effect e riemette,
      // quindi l'effect si ri-esegue subito dopo aver lanciato la fetch; una
      // cleanup che annulla ucciderebbe la richiesta appena partita e la sessione
      // non verrebbe mai popolata. Confrontare la chiave risponde invece alla
      // domanda giusta: «questa risposta vale ancora per il token e il nucleo
      // correnti?». Il logout azzera la chiave, e le risposte in volo decadono.
      fetchMe(user.access_token)
        .then((me) => {
          if (!mounted.current || lastFetchKey.current !== fetchKey) return;
          setMaintenance(null);
          setAuthentication(user.access_token, me.roles ?? [], me.privileges ?? []);
        })
        .catch((error) => {
          if (!mounted.current || lastFetchKey.current !== fetchKey) return;
          console.error("Failed to fetch /me", error);

          // Il 503 di manutenzione del proxy durante un rilascio non è un guasto
          // della sessione: il token è buono e fra poco `/me` risponderà. Azzerarla
          // manderebbe a /login senza un perché. Si mostra la manutenzione e si
          // ritenta da soli dopo `Retry-After` — l'unica eccezione alla regola del
          // «non si ritenta», ammessa perché `/me` è una lettura e il ritmo lo dà
          // il proxy. La chiave si sblocca solo allo scadere, non subito: un giro
          // dell'effect nel frattempo non deve rilanciare la richiesta.
          const retryAfter: number | null = maintenanceRetryAfter(error);
          if (retryAfter !== null) {
            setMaintenance(retryAfter);
            if (retryTimer.current) clearTimeout(retryTimer.current);
            retryTimer.current = setTimeout(() => {
              lastFetchKey.current = null;
              setRetryTick((tick) => tick + 1);
            }, retryAfter * 1000);
            return;
          }

          clearAuthentication();

          // NON si azzerano qui i sub-tenant, per due ragioni. La prima è di
          // merito: quell'elenco viene dal claim del token, non da `/me`, e sui
          // due codici terminali è l'unica via d'uscita che resta all'utente —
          // toglierlo lo chiuderebbe nel giro login → 404 → login. La seconda è
          // meccanica: `clearSubTenants` azzera anche `selected`, che è una
          // dipendenza di questo effect, e il giro successivo lo ri-riconcilia
          // dal claim → rifetch → errore → azzeramento, all'infinito. Il logout
          // (ramo non autenticato, sotto) resta il posto dove si azzera.
          //
          // La chiave NON viene sbloccata: un errore non deve innescare un
          // ritentativo automatico sullo stesso token e nucleo. La sessione è
          // stata azzerata, quindi si passa da /login e il token nuovo produce
          // una chiave nuova.
          const problem = terminalProblem(error);
          if (problem) setSubTenantProblem(problem);
        })
        .finally(() => {
          // Chiude la sincronizzazione solo se questa è ancora la fetch corrente
          // (o se non c'è più nessuna fetch): se è stata superata, è la più
          // recente a doverla chiudere, non questa.
          if (mounted.current && (lastFetchKey.current === fetchKey || lastFetchKey.current === null))
            endSubTenantSync();
        });
      return;
    }

    lastFetchKey.current = null;
    clearAuthentication();
    clearSubTenants();
  }, [auth.user, auth.isAuthenticated, auth.isLoading, auth.activeNavigator, selected, retryTick]);

  if (maintenance !== null) return <MaintenanceScreen seconds={maintenance} />;
  return <>{children}</>;
}

function MaintenanceScreen({ seconds }: Readonly<{ seconds: number }>): ReactElement {
  const { t } = useTranslation();
  return (
    <Result
      status="warning"
      title={t("system.maintenance.title")}
      subTitle={t("system.maintenance.retrying", { minutes: maintenanceMinutes(seconds) })}
    />
  );
}
